import React from 'react';
import {useRef} from 'react';

import {motion} from 'framer-motion';
import {useInView} from 'framer-motion';
import {useIntl} from 'react-intl';

export const HealthSection: React.FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useIntl();
  const ref = useRef(null);
  const isInView = useInView(ref, {once: true, amount: 0.3});

  const features = [
    {
      title: intl.formatMessage({id: 'home.healthSection.feature1Title'}),
      description: intl.formatMessage({
        id: 'home.healthSection.feature1Description',
      }),
      image: '/images/home/health1.png',
    },
    {
      title: intl.formatMessage({id: 'home.healthSection.feature2Title'}),
      description: intl.formatMessage({
        id: 'home.healthSection.feature2Description',
      }),
      image: '/images/home/health2.png',
    },
    {
      title: intl.formatMessage({id: 'home.healthSection.feature3Title'}),
      description: intl.formatMessage({
        id: 'home.healthSection.feature3Description',
      }),
      image: '/images/home/health3.png',
    },
    {
      title: intl.formatMessage({id: 'home.healthSection.feature4Title'}),
      description: intl.formatMessage({
        id: 'home.healthSection.feature4Description',
      }),
      image: '/images/home/health4.png',
    },
    {
      title: intl.formatMessage({id: 'home.healthSection.feature5Title'}),
      description: intl.formatMessage({
        id: 'home.healthSection.feature5Description',
      }),
      image: '/images/home/health5.png',
    },
    {
      title: intl.formatMessage({id: 'home.healthSection.feature6Title'}),
      description: intl.formatMessage({
        id: 'home.healthSection.feature6Description',
      }),
      image: '/images/home/health6.png',
    },
  ];

  return (
    <section ref={ref} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Left Side - Headlines */}
          <motion.div
          // initial={{opacity: 0, x: -50}}
          // animate={isInView ? {opacity: 1, x: 0} : {}}
          // transition={{duration: 0.8, ease: 'easeOut'}}
          >
            <motion.h3
              className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4"
              // initial={{opacity: 0, y: 20}}
              // animate={isInView ? {opacity: 1, y: 0} : {}}
              // transition={{duration: 0.6, delay: 0.2}}
            >
              {intl.formatMessage({id: 'home.healthSection.title'})}
            </motion.h3>

            <motion.h2
              className="text-5xl md:text-7xl font-light text-black leading-tight mb-8"
              // initial={{opacity: 0, y: 30}}
              // animate={isInView ? {opacity: 1, y: 0} : {}}
              // transition={{duration: 0.8, delay: 0.3}}
            >
              {intl.formatMessage({id: 'home.healthSection.description'})}
            </motion.h2>
          </motion.div>

          {/* Right Side - Description */}
          <motion.div
            className="flex flex-col justify-center"
            // initial={{opacity: 0, x: 50}}
            // animate={isInView ? {opacity: 1, x: 0} : {}}
            // transition={{duration: 0.8, delay: 0.4}}
          >
            <motion.p
              className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8"
              // initial={{opacity: 0, y: 20}}
              // animate={isInView ? {opacity: 1, y: 0} : {}}
              // transition={{duration: 0.6, delay: 0.5}}
            >
              {intl.formatMessage({id: 'home.healthSection.subtitle'})}
            </motion.p>

            <motion.a
              href="https://blog.stryd.com/2022/12/09/impact-loading-rate-lower-body-stress-score/"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-lg group"
              // initial={{opacity: 0, y: 20}}
              // animate={isInView ? {opacity: 1, y: 0} : {}}
              // transition={{duration: 0.6, delay: 0.6}}
            >
              {intl.formatMessage({id: 'home.healthSection.learnMore'})}
              <motion.svg
                className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </motion.svg>
            </motion.a>
          </motion.div>
        </div>

        {/* Hero Image Section */}
        <motion.div
          className="relative"
          // initial={{opacity: 0, y: 50}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 1, delay: 0.7}}
        >
          <div className="relative overflow-hidden rounded-3xl">
            <img
              src="/images/hero-placeholder.png"
              alt="Runner monitoring their health with Stryd 5.0"
              className="w-full h-96 md:h-[500px] object-cover"
            />

            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />

            {/* Floating metrics overlay */}
            <motion.div
              className="absolute top-8 right-8 bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
              // initial={{opacity: 0, scale: 0.8}}
              // animate={isInView ? {opacity: 1, scale: 1} : {}}
              // transition={{duration: 0.8, delay: 1.0}}
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {intl.formatMessage({id: 'home.healthSection.heroTitle'})}
                </div>
                <div className="text-sm text-gray-600">
                  {intl.formatMessage({id: 'home.healthSection.heroSubtitle'})}
                </div>
              </div>
            </motion.div>

            <motion.div
              className="absolute bottom-8 left-8 bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
              // initial={{opacity: 0, scale: 0.8}}
              // animate={isInView ? {opacity: 1, scale: 1} : {}}
              // transition={{duration: 0.8, delay: 1.2}}
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-1">
                  {intl.formatMessage({id: 'home.healthSection.heroTitle2'})}
                </div>
                <div className="text-sm text-gray-600">
                  {intl.formatMessage({id: 'home.healthSection.heroSubtitle2'})}
                </div>
              </div>
            </motion.div>

            {/* Animated sound wave effect */}
            <motion.div
              className="absolute top-1/2 right-1/4 w-16 h-16"
              initial={{opacity: 0, scale: 0}}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 1.2],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: 1.5,
              }}
            >
              <div className="w-full h-full border-2 border-blue-400 rounded-full" />
            </motion.div>

            <motion.div
              className="absolute top-1/2 right-1/4 w-24 h-24"
              initial={{opacity: 0, scale: 0}}
              animate={{
                opacity: [0, 0.7, 0],
                scale: [0, 1, 1.2],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: 1.7,
              }}
            >
              <div className="w-full h-full border-2 border-blue-300 rounded-full" />
            </motion.div>

            <motion.div
              className="absolute top-1/2 right-1/4 w-32 h-32"
              initial={{opacity: 0, scale: 0}}
              animate={{
                opacity: [0, 0.4, 0],
                scale: [0, 1, 1.2],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: 1.9,
              }}
            >
              <div className="w-full h-full border-2 border-blue-200 rounded-full" />
            </motion.div>
          </div>
        </motion.div>

        {/* Feature Cards - Horizontal Scroll */}
        <div className="py-20">
          <div
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
            style={{scrollSnapType: 'x mandatory'}}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="flex-shrink-0 w-80 bg-white rounded-3xl overflow-hidden shadow-sm transition-all duration-300 hover:shadow-lg"
                style={{scrollSnapAlign: 'start'}}
                // initial={{opacity: 0, y: 30}}
                // animate={isInView ? {opacity: 1, y: 0} : {}}
                // transition={{duration: 0.6, delay: 0.4 + index * 0.1}}
              >
                {/* Image */}
                <div className="overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-gray-600 leading-relaxed text-base">
                    <span className="font-semibold pr-2">{feature.title}</span>
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Scroll indicator */}
          <div className="flex justify-center mt-6">
            <div className="flex gap-2">
              {features.map((_, index) => (
                <div key={index} className="w-2 h-2 bg-gray-300 rounded-full" />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        {/* <motion.div
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
          initial={{opacity: 0, y: 30}}
          animate={isInView ? {opacity: 1, y: 0} : {}}
          transition={{duration: 0.8, delay: 1.0}}
        >
          <div className="text-center">
            <motion.div
              className="text-4xl font-bold text-blue-600 mb-2"
              initial={{scale: 0}}
              animate={isInView ? {scale: 1} : {}}
              transition={{duration: 0.6, delay: 1.2}}
            >
              99.5%
            </motion.div>
            <div className="text-gray-600 font-medium">Power Accuracy</div>
            <div className="text-sm text-gray-500 mt-1">
              Scientifically validated metrics
            </div>
          </div>

          <div className="text-center">
            <motion.div
              className="text-4xl font-bold text-green-600 mb-2"
              initial={{scale: 0}}
              animate={isInView ? {scale: 1} : {}}
              transition={{duration: 0.6, delay: 1.4}}
            >
              40%
            </motion.div>
            <div className="text-gray-600 font-medium">Faster Response</div>
            <div className="text-sm text-gray-500 mt-1">
              On climbs and descents
            </div>
          </div>

          <div className="text-center">
            <motion.div
              className="text-4xl font-bold text-purple-600 mb-2"
              initial={{scale: 0}}
              animate={isInView ? {scale: 1} : {}}
              transition={{duration: 0.6, delay: 1.6}}
            >
              2x
            </motion.div>
            <div className="text-gray-600 font-medium">Processing Speed</div>
            <div className="text-sm text-gray-500 mt-1">Refined algorithms</div>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};
